import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVideoStream } from "../../providers/videoStreams";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { FiUpload, FiLink } from "react-icons/fi";
import PageHeader from "../../components/pageHeader";
import { Container } from "./styles";

const CreateVideoStream = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUploadUrl, uploadVideoFile, getAllVideoStreams } =
    useVideoStream();
  const { path } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [videoName, setVideoName] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError("");
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);

      // Auto-fill name from filename if empty
      if (!videoName) {
        const fileName = files[0].name.replace(/\.[^/.]+$/, "");
        setVideoName(fileName);
      }
    }
  };

  const validateForm = () => {
    if (!videoName.trim()) {
      setError(t("video_name_required"));
      return false;
    }

    if (tabValue === 0 && !selectedFile) {
      setError(t("video_file_required"));
      return false;
    }

    if (tabValue === 1 && !videoUrl.trim()) {
      setError(t("video_url_required"));
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    setError("");

    try {
      if (tabValue === 0 && selectedFile) {
        // Direct file upload
        const uploadData = await getUploadUrl({
          name: videoName,
          description: videoDescription,
        });

        if (!uploadData) {
          throw new Error(t("failed_to_get_upload_url"));
        }

        const uploadSuccess = await uploadVideoFile(
          uploadData.uploadURL,
          selectedFile
        );

        if (!uploadSuccess) {
          throw new Error(t("upload_failed"));
        }
        getAllVideoStreams();
        // Navigate to video list on success
        navigate(`/${path}/video-streams`);
      } else if (tabValue === 1) {
        // URL upload handled by backend
        const uploadData = await getUploadUrl({
          name: videoName,
          description: videoDescription,
          sourceUrl: videoUrl,
        });

        if (!uploadData) {
          throw new Error(t("failed_to_process_video_url"));
        }
        getAllVideoStreams();
        // Navigate to video list on success
        navigate(`/${path}/video-streams`);
      }
    } catch (err: any) {
      setError(err.message || t("upload_error"));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Container>
      <PageHeader
        title={t("upload_video")}
        description={t("upload_video_description")}
        buttonContent={t("back_to_videos")}
        buttonPath="/video-streams"
        icon={<FiUpload />}
      />

      <Paper className="uploadContainer">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<FiUpload />} label={t("file_upload")} />
          <Tab icon={<FiLink />} label={t("url_upload")} />
        </Tabs>

        <Box p={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label={t("video_name")}
            fullWidth
            margin="normal"
            value={videoName}
            onChange={(e) => setVideoName(e.target.value)}
            required
          />

          <TextField
            label={t("video_description")}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={videoDescription}
            onChange={(e) => setVideoDescription(e.target.value)}
          />

          {tabValue === 0 ? (
            <div className="fileUploadSection">
              <input
                type="file"
                id="video-upload-input"
                accept="video/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              <label htmlFor="video-upload-input" className="uploadArea">
                {selectedFile ? (
                  <div className="selectedFileInfo">
                    <Typography variant="body1" fontWeight="bold">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </div>
                ) : (
                  <>
                    <FiUpload size={40} />
                    <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                      {t("drag_drop_or_click")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("supported_formats")}
                    </Typography>
                  </>
                )}
              </label>
            </div>
          ) : (
            <TextField
              label={t("video_url")}
              placeholder="https://example.com/video.mp4"
              fullWidth
              margin="normal"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              helperText={t("url_upload_help")}
              required
            />
          )}

          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate(`/${path}/video-streams`)}
              sx={{ mr: 2 }}
              disabled={isUploading}
            >
              {t("cancel")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={20} /> : null}
            >
              {isUploading ? t("uploading") : t("upload")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateVideoStream;
